import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import useAppStore from '../context/AppContext';
import { validateTransactions, validateWithOverride, challengeRerun } from '../services/api';

function Preview() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [validating, setValidating] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Balance override state
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [dialogFilename, setDialogFilename] = useState(null);
  const [dialogValues, setDialogValues] = useState({ opening: '', closing: '' });
  const [dialogTransactions, setDialogTransactions] = useState([]);

  // Challenge rerun state
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  const [rerunFilename, setRerunFilename] = useState(null);
  const [selectedRerunModel, setSelectedRerunModel] = useState('openai/gpt-5-mini');
  const [pendingRevalidation, setPendingRevalidation] = useState(false);

  const {
    extractedData,
    validationReport,
    setValidationReport,
    hasValidationErrors,
    balanceOverrides,
    setBalanceOverride,
    clearBalanceOverride,
    setExtractedData,
    rerunStatus,
    setRerunStatus,
    updateExtractedDataForFile,
    availableModels,
  } = useAppStore();

  // Flatten all transactions for display
  const allTransactions = useMemo(() => {
    return extractedData.flatMap((data, idx) =>
      (data.transactions || []).map((tx, txIdx) => ({
        ...tx,
        _source: data.filename,
        _id: `${idx}-${txIdx}`,
      }))
    );
  }, [extractedData]);

  // Run validation on mount
  useEffect(() => {
    if (extractedData.length === 0) {
      navigate('/');
      return;
    }

    const runValidation = async () => {
      setValidating(true);

      try {
        // Validate each statement
        const reports = [];
        for (const data of extractedData) {
          // Map snake_case metadata from AI to camelCase expected by validator
          const mappedMetadata = {
            bankName: data.metadata?.bank_name,
            accountNumber: data.metadata?.account_number,
            statementPeriod: data.metadata?.statement_period_start && data.metadata?.statement_period_end
              ? `${data.metadata.statement_period_start} - ${data.metadata.statement_period_end}`
              : null,
            openingBalance: data.metadata?.opening_balance,
            closingBalance: data.metadata?.closing_balance,
            currency: data.metadata?.currency,
          };
          const result = await validateTransactions(mappedMetadata, data.transactions);
          if (result.success) {
            reports.push({
              filename: data.filename,
              ...result.report,
            });
          }
        }

        // Combine reports
        const combined = {
          summary: {
            totalStatements: reports.length,
            totalTransactions: allTransactions.length,
            errorCount: reports.reduce((sum, r) => sum + (r.summary?.errorCount || 0), 0),
            warningCount: reports.reduce((sum, r) => sum + (r.summary?.warningCount || 0), 0),
          },
          errors: reports.flatMap((r) => r.errors || []),
          warnings: reports.flatMap((r) => r.warnings || []),
          balanceChecks: reports.map((r) => ({
            filename: r.filename,
            ...r.balanceCheck,
          })),
        };

        setValidationReport(combined);

        if (combined.summary.errorCount > 0) {
          enqueueSnackbar(`${combined.summary.errorCount} validation error(s) found`, { variant: 'error' });
        } else if (combined.summary.warningCount > 0) {
          enqueueSnackbar(`${combined.summary.warningCount} warning(s) - review recommended`, { variant: 'warning' });
        } else {
          enqueueSnackbar('Validation passed!', { variant: 'success' });
        }
      } catch (error) {
        console.error('Validation error:', error);
        enqueueSnackbar('Validation failed', { variant: 'error' });
      } finally {
        setValidating(false);
      }
    };

    runValidation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to handle pending revalidation after extractedData updates
  useEffect(() => {
    if (pendingRevalidation && extractedData.length > 0) {
      setPendingRevalidation(false);
      handleRevalidate();
    }
  }, [pendingRevalidation, extractedData]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    const formatted = Math.abs(num).toFixed(2);
    return num < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTransactions = allTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Open override dialog with transactions
  const handleOpenOverrideDialog = (filename, currentOpening, currentClosing) => {
    const override = balanceOverrides[filename];
    const fileData = extractedData.find((d) => d.filename === filename);
    const transactions = fileData?.transactions || [];

    setDialogFilename(filename);
    setDialogValues({
      opening: override?.opening?.toString() ?? currentOpening?.toString() ?? '',
      closing: override?.closing?.toString() ?? currentClosing?.toString() ?? '',
    });
    setDialogTransactions(transactions.map((tx, idx) => ({ ...tx, _idx: idx })));
    setOverrideDialogOpen(true);
  };

  // Update transaction in dialog
  const handleUpdateDialogTransaction = (idx, field, value) => {
    setDialogTransactions((prev) =>
      prev.map((tx) => (tx._idx === idx ? { ...tx, [field]: value } : tx))
    );
  };

  // Delete transaction in dialog
  const handleDeleteDialogTransaction = (idx) => {
    setDialogTransactions((prev) => prev.filter((tx) => tx._idx !== idx));
  };

  // Calculate dialog totals
  const dialogTransactionSum = useMemo(() => {
    return dialogTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
  }, [dialogTransactions]);

  const dialogCalculatedClosing = useMemo(() => {
    const opening = parseFloat(dialogValues.opening) || 0;
    return opening + dialogTransactionSum;
  }, [dialogValues.opening, dialogTransactionSum]);

  const dialogBalanceMatches = useMemo(() => {
    const closing = parseFloat(dialogValues.closing) || 0;
    return Math.abs(dialogCalculatedClosing - closing) <= 0.01;
  }, [dialogCalculatedClosing, dialogValues.closing]);

  // Save from dialog - saves both balances and transactions
  const handleSaveOverrideDialog = () => {
    const opening = parseFloat(dialogValues.opening);
    const closing = parseFloat(dialogValues.closing);

    if (isNaN(opening) || isNaN(closing)) {
      enqueueSnackbar('Please enter valid numbers for balances', { variant: 'error' });
      return;
    }

    // Update the transactions in extractedData (remove _idx helper field)
    const cleanedTransactions = dialogTransactions.map(({ _idx, ...tx }) => tx);
    updateExtractedDataForFile(dialogFilename, {
      transactions: cleanedTransactions,
      metadata: {
        ...extractedData.find((d) => d.filename === dialogFilename)?.metadata,
        opening_balance: opening,
        closing_balance: closing,
      },
    });

    // Set balance override for validation
    setBalanceOverride(dialogFilename, opening, closing);
    setOverrideDialogOpen(false);
    enqueueSnackbar('Changes saved - click Re-validate to apply', { variant: 'info' });
  };

  // Re-validate with overrides
  const handleRevalidate = async () => {
    setValidating(true);

    try {
      const reports = [];
      for (const data of extractedData) {
        const override = balanceOverrides[data.filename];
        const mappedMetadata = {
          bankName: data.metadata?.bank_name,
          accountNumber: data.metadata?.account_number,
          statementPeriod: data.metadata?.statement_period_start && data.metadata?.statement_period_end
            ? `${data.metadata.statement_period_start} - ${data.metadata.statement_period_end}`
            : null,
          openingBalance: data.metadata?.opening_balance,
          closingBalance: data.metadata?.closing_balance,
          currency: data.metadata?.currency,
        };

        let result;
        if (override) {
          // Use override validation
          result = await validateWithOverride(mappedMetadata, data.transactions, override);
        } else {
          result = await validateTransactions(mappedMetadata, data.transactions);
        }

        if (result.success) {
          reports.push({
            filename: data.filename,
            ...result.report,
            hasOverride: !!override,
          });
        }
      }

      const combined = {
        summary: {
          totalStatements: reports.length,
          totalTransactions: allTransactions.length,
          errorCount: reports.reduce((sum, r) => sum + (r.summary?.errorCount || 0), 0),
          warningCount: reports.reduce((sum, r) => sum + (r.summary?.warningCount || 0), 0),
        },
        errors: reports.flatMap((r) => r.errors || []),
        warnings: reports.flatMap((r) => r.warnings || []),
        balanceChecks: reports.map((r) => ({
          filename: r.filename,
          ...r.balanceCheck,
          hasOverride: r.hasOverride,
          balanceOverride: r.balanceOverride,
        })),
      };

      setValidationReport(combined);

      if (combined.summary.errorCount > 0) {
        enqueueSnackbar(`${combined.summary.errorCount} validation error(s) found`, { variant: 'error' });
      } else if (combined.summary.warningCount > 0) {
        enqueueSnackbar(`Validation passed with ${combined.summary.warningCount} warning(s)`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Validation passed!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Re-validation error:', error);
      enqueueSnackbar('Re-validation failed', { variant: 'error' });
    } finally {
      setValidating(false);
    }
  };

  // Open challenge rerun dialog
  const handleOpenRerunDialog = (filename) => {
    setRerunFilename(filename);
    setSelectedRerunModel('openai/gpt-5-mini');
    setRerunDialogOpen(true);
  };

  // Execute challenge rerun
  const handleChallengeRerun = async () => {
    if (!rerunFilename) return;

    setRerunDialogOpen(false);
    setRerunStatus(rerunFilename, 'running');

    try {
      // Get current balance check for this file
      const balanceCheck = validationReport?.balanceChecks?.find((c) => c.filename === rerunFilename);

      // Build error context
      const errorContext = {
        balanceMismatch: balanceCheck ? {
          opening: balanceCheck.opening,
          closing: balanceCheck.closing,
          calculated: balanceCheck.calculated,
          difference: balanceCheck.difference,
          transactionSum: balanceCheck.calculated !== null && balanceCheck.opening !== null
            ? balanceCheck.calculated - balanceCheck.opening
            : null,
        } : null,
        errors: validationReport?.errors?.filter((e) => e.filename === rerunFilename) || [],
      };

      enqueueSnackbar(`Re-extracting ${rerunFilename} with ${selectedRerunModel}...`, { variant: 'info' });

      // The filename in extractedData IS the server filename
      const result = await challengeRerun(rerunFilename, errorContext, {
        model: selectedRerunModel,
      });

      if (result.success) {
        // Update extracted data with new results
        updateExtractedDataForFile(rerunFilename, {
          metadata: result.result.metadata,
          transactions: result.result.transactions,
        });

        setRerunStatus(rerunFilename, 'complete');
        enqueueSnackbar(`Challenge rerun successful - ${result.result.transactionCount} transactions extracted`, { variant: 'success' });

        // Clear any balance override for this file since we have new data
        clearBalanceOverride(rerunFilename);

        // Mark that we need to re-validate after state update
        setPendingRevalidation(true);
      } else {
        setRerunStatus(rerunFilename, 'failed');
        enqueueSnackbar(`Challenge rerun failed: ${result.error}`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Challenge rerun error:', error);
      setRerunStatus(rerunFilename, 'failed');
      enqueueSnackbar(`Challenge rerun failed: ${error.message}`, { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Review Transactions
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Statements
              </Typography>
              <Typography variant="h4">{extractedData.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Transactions
              </Typography>
              <Typography variant="h4">{allTransactions.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Errors
              </Typography>
              <Typography variant="h4" color={validationReport?.summary?.errorCount > 0 ? 'error' : 'inherit'}>
                {validationReport?.summary?.errorCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Warnings
              </Typography>
              <Typography variant="h4" color={validationReport?.summary?.warningCount > 0 ? 'warning.main' : 'inherit'}>
                {validationReport?.summary?.warningCount || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Validation Status */}
      {validating ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Validating transactions...
        </Alert>
      ) : validationReport ? (
        <Alert
          severity={
            hasValidationErrors ? 'error' : validationReport.summary?.warningCount > 0 ? 'warning' : 'success'
          }
          icon={
            hasValidationErrors ? <ErrorIcon /> : validationReport.summary?.warningCount > 0 ? <WarningIcon /> : <CheckCircleIcon />
          }
          sx={{ mb: 3 }}
        >
          {hasValidationErrors
            ? 'Validation errors found - please review before exporting'
            : validationReport.summary?.warningCount > 0
            ? 'Validation passed with warnings'
            : 'All validations passed'}
        </Alert>
      ) : null}

      {/* Balance Reconciliation */}
      {validationReport?.balanceChecks?.length > 0 && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography>Balance Reconciliation</Typography>
              {Object.keys(balanceOverrides).length > 0 && (
                <Chip label={`${Object.keys(balanceOverrides).length} override(s)`} size="small" color="info" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRevalidate}
                disabled={validating}
                startIcon={validating ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                Re-validate
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Statement</TableCell>
                    <TableCell align="right">Opening Balance</TableCell>
                    <TableCell align="right">Transactions Sum</TableCell>
                    <TableCell align="right">Calculated Closing</TableCell>
                    <TableCell align="right">Actual Closing</TableCell>
                    <TableCell align="right">Difference</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationReport.balanceChecks.map((check, idx) => {
                    const transactionSum = check.calculated !== null && check.opening !== null
                      ? check.calculated - check.opening
                      : null;
                    const hasOverride = !!balanceOverrides[check.filename];
                    const isRerunning = rerunStatus[check.filename] === 'running';

                    return (
                      <TableRow key={idx} sx={hasOverride ? { bgcolor: 'action.hover' } : {}}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={check.filename} size="small" variant="outlined" />
                            {hasOverride && (
                              <Chip label="Modified" size="small" color="info" variant="outlined" />
                            )}
                            {isRerunning && <CircularProgress size={16} />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {check.opening !== null ? `$${parseFloat(check.opening).toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {transactionSum !== null ? `$${transactionSum.toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {check.calculated !== null ? `$${parseFloat(check.calculated).toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {check.closing !== null ? `$${parseFloat(check.closing).toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: check.difference > 0.01 ? 'error.main' : 'inherit' }}>
                          {check.difference !== null ? `$${parseFloat(check.difference).toFixed(2)}` : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          {check.valid === null ? (
                            <Chip label="No balance data" size="small" color="default" />
                          ) : check.valid ? (
                            <Chip label="Reconciled" size="small" color="success" icon={<CheckCircleIcon />} />
                          ) : (
                            <Chip label="Mismatch" size="small" color="error" icon={<ErrorIcon />} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit balances & transactions">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenOverrideDialog(check.filename, check.opening, check.closing)}
                                disabled={isRerunning}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!check.valid && (
                              <Tooltip title="Challenge rerun with AI">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleOpenRerunDialog(check.filename)}
                                  disabled={isRerunning}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Validation Details */}
      {validationReport && (validationReport.errors?.length > 0 || validationReport.warnings?.length > 0) && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Validation Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {validationReport.errors?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors:
                </Typography>
                {validationReport.errors.map((err, idx) => (
                  <Chip
                    key={idx}
                    label={err.message || err}
                    color="error"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
            {validationReport.warnings?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Warnings:
                </Typography>
                {validationReport.warnings.map((warn, idx) => (
                  <Chip
                    key={idx}
                    label={warn.message || warn}
                    color="warning"
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Transactions Table */}
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((tx) => (
                <TableRow key={tx._id} hover>
                  <TableCell>{tx.date}</TableCell>
                  <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.description}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: parseFloat(tx.amount) < 0 ? 'error.main' : 'success.main' }}
                  >
                    {formatAmount(tx.amount)}
                  </TableCell>
                  <TableCell>
                    <Chip label={tx._source} size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={allTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/processing')}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/export')}
          disabled={hasValidationErrors}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Balance Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Statement: {dialogFilename}</DialogTitle>
        <DialogContent>
          {/* Balance Summary */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: dialogBalanceMatches ? 'success.light' : 'error.light' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Opening Balance"
                  type="number"
                  size="small"
                  value={dialogValues.opening}
                  onChange={(e) => setDialogValues({ ...dialogValues, opening: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Transaction Sum</Typography>
                <Typography variant="h6">${dialogTransactionSum.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">Calculated Closing</Typography>
                <Typography variant="h6">${dialogCalculatedClosing.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Actual Closing"
                  type="number"
                  size="small"
                  value={dialogValues.closing}
                  onChange={(e) => setDialogValues({ ...dialogValues, closing: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              {dialogBalanceMatches ? (
                <Chip label="✓ Balance Matches" color="success" />
              ) : (
                <Chip
                  label={`✗ Difference: $${Math.abs(dialogCalculatedClosing - (parseFloat(dialogValues.closing) || 0)).toFixed(2)}`}
                  color="error"
                />
              )}
            </Box>
          </Paper>

          {/* Transactions Table */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Transactions ({dialogTransactions.length})
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 120 }}>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sx={{ width: 120 }} align="right">Amount</TableCell>
                  <TableCell sx={{ width: 60 }} align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogTransactions.map((tx) => (
                  <TableRow key={tx._idx} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        value={tx.date || ''}
                        onChange={(e) => handleUpdateDialogTransaction(tx._idx, 'date', e.target.value)}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={tx.description || ''}
                        onChange={(e) => handleUpdateDialogTransaction(tx._idx, 'description', e.target.value)}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={tx.amount || ''}
                        onChange={(e) => handleUpdateDialogTransaction(tx._idx, 'amount', e.target.value)}
                        variant="standard"
                        inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                        sx={{
                          width: 100,
                          '& input': { color: parseFloat(tx.amount) < 0 ? 'error.main' : 'success.main' },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDialogTransaction(tx._idx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mt: 2 }}>
            Edit amounts, dates, or descriptions directly. Delete incorrect transactions. The balance will update automatically.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveOverrideDialog}
            variant="contained"
            color={dialogBalanceMatches ? 'success' : 'warning'}
          >
            {dialogBalanceMatches ? 'Save Changes' : 'Save Anyway (Unbalanced)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Challenge Rerun Dialog */}
      <Dialog open={rerunDialogOpen} onClose={() => setRerunDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Challenge Rerun</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Re-extract <strong>{rerunFilename}</strong> using AI with error context.
            The model will be informed about the reconciliation issues and will try to extract more accurately.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will send the file back to the AI for re-extraction. The current validation errors will be included
            as context to help the model identify what went wrong.
          </Alert>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Model</InputLabel>
            <Select
              value={selectedRerunModel}
              onChange={(e) => setSelectedRerunModel(e.target.value)}
              label="Model"
            >
              <MenuItem value="openai/gpt-5-mini">GPT-5.1 Mini (Recommended)</MenuItem>
              {availableModels
                .filter((m) => m.id !== 'openai/gpt-5-mini')
                .map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerunDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleChallengeRerun} variant="contained" color="warning">
            Start Rerun
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Preview;
